/**
 * A single checkbox, which the suite had no wrapper for.
 *
 * The screens that needed one imported react-daisyui's directly, which is the
 * one library import `ui/` exists to hold. `onChange` hands over the new state
 * rather than an event, matching every other control here.
 *
 * @module components/ui/Checkbox
 */

import React, { type ReactNode, useId } from "react";

import { useLockedRefusal } from "./EditLock";
import { LabelledControl } from "./LabelledControl";

/** The sizes, exported so safelist.test.ts can walk them. */
export const CHECKBOX_SIZES = ["sm", "md"] as const;
export type CheckboxSize = (typeof CHECKBOX_SIZES)[number];

const SIZE_CLASS: Record<CheckboxSize, string> = { sm: "checkbox-sm", md: "" };

/** The colours a ticked box can fill with, exported so safelist.test.ts can walk them. */
export const CHECKBOX_TONES = ["default", "success"] as const;
export type CheckboxTone = (typeof CHECKBOX_TONES)[number];

// On `checked:` only, so an empty box keeps the neutral edge every other box has.
const TONE_CLASS: Record<CheckboxTone, string> = {
  default: "",
  success: "checked:checkbox-success",
};

export interface CheckboxProps {
  /** Set by the caller when it draws its own `<label for>`; otherwise generated. */
  id?: string;
  /**
   * The visible label, drawn beside the box and wired through `htmlFor`. The
   * preferred way to name it; `aria-label` is for a box with no room for text.
   */
  label?: ReactNode;
  /** A line under the label, announced with the box through `aria-describedby`. */
  description?: ReactNode;
  checked: boolean;
  /**
   * Partly checked: some of what this box stands for is selected. Only the
   * DOM property carries it, so it is set through a ref rather than an
   * attribute, and `aria-checked="mixed"` says the same thing out loud.
   */
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Names it when no visible text sits beside it, as in a matrix cell where
   * the row and column headings are the only context.
   */
  "aria-label"?: string;
  /** Ids of the text that qualifies it, such as a caller's own disabled reason. */
  "aria-describedby"?: string;
  disabled?: boolean;
  /** Why it is refused: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  size?: CheckboxSize;
  /** The fill once ticked: `success` where ticking means something is done or in. */
  tone?: CheckboxTone;
  /** Why it is disabled, for a pointer. Never the only place a reason lives. */
  title?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <Checkbox checked={on} onChange={setOn} aria-label="Programme Lead for Ada" />
 * ```
 */
export function Checkbox({
  id = undefined,
  checked,
  indeterminate = false,
  onChange,
  label = undefined,
  description = undefined,
  "aria-label": ariaLabel = undefined,
  "aria-describedby": describedBy = undefined,
  disabled: ownDisabled = false,
  size = "md",
  tone = "default",
  title = undefined,
  className = "",
  "data-testid": testId,
  ...reasonProps
}: CheckboxProps): React.ReactElement {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the control is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabled, offersReason, governed, disabledReason } = useLockedRefusal(
    ownDisabled,
    reasonProps,
  );
  const reasonId = useId();
  const generatedId = useId();
  const descriptionId = useId();
  const controlId = id ?? generatedId;
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const described =
    [describedBy, description ? descriptionId : undefined, reason ? reasonId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  // `aria-disabled`, not `disabled`: a refused box keeps the reason in its
  // description, and a natively disabled control leaves the tab order with it. Same
  // pattern as `Button`. daisyUI paints the refusal from `.checkbox:disabled`
  // only (read in its own checkbox.css), so the fade is restored here.
  const refused = disabled ? "opacity-20 cursor-not-allowed" : "";
  const box = (
    <input
      type="checkbox"
      id={controlId}
      ref={(element) => {
        if (element) {
          // eslint-disable-next-line no-param-reassign -- `indeterminate` is a DOM property with no attribute, so the node is the only place it can be set. Exit: none.
          element.indeterminate = indeterminate;
        }
      }}
      aria-checked={indeterminate ? "mixed" : undefined}
      className={`checkbox ${SIZE_CLASS[size]} ${TONE_CLASS[tone]} ${refused} ${className}`
        .replace(/\s+/g, " ")
        .trim()}
      checked={checked}
      aria-label={ariaLabel}
      aria-describedby={described}
      title={title}
      aria-disabled={disabled || undefined}
      onChange={(event) => {
        if (disabled) {
          return;
        }
        onChange(event.currentTarget.checked);
      }}
      data-testid={testId}
    />
  );
  // Under a lock provider the wrapper is always there, so closing the lock
  // does not remount the control and drop its focus.
  const control =
    offersReason || governed ? (
      <span className={reason ? "tooltip" : "contents"} data-tip={reason}>
        {box}
        {reason ? (
          <span id={reasonId} className="sr-only">
            {reason}
          </span>
        ) : null}
      </span>
    ) : (
      box
    );
  if (label === undefined && description === undefined) {
    return control;
  }
  return (
    <LabelledControl
      controlId={controlId}
      label={label}
      descriptionId={descriptionId}
      description={description}
    >
      {control}
    </LabelledControl>
  );
}
